import axiosInstance from "axiosConfig";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

export const SESSION_QUERY_KEY = ["successors", "session"] as const;

export type SuccessorSessionResponse =
  | { status: "success"; authorized: true; email?: string }
  | { status: "unauthorized" | "forbidden" | "error"; authorized: false; reason?: string };

export const fetchSuccessorSession = async (): Promise<SuccessorSessionResponse> => {
  try {
    const { data } = await axiosInstance.get("/successors/session");

    const isAuth = (data?.authorized ?? data?.isAuthorized) === true;

    if (isAuth) {
      return { status: "success", authorized: true, email: data?.email };
    }
    return {
      status: "unauthorized",
      authorized: false,
      reason: data?.message ?? "Not authorized",
    };
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 401 || status === 403) {
      return {
        status: status === 401 ? "unauthorized" : "forbidden",
        authorized: false,
        reason: e?.response?.data?.message ?? "Not authorized",
      };
    }
    throw e;
  }
};

export const useCheckSuccessorSession = (
  options?: UseQueryOptions<SuccessorSessionResponse, any>
) => {
  return useQuery<SuccessorSessionResponse, any>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSuccessorSession,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    retry: false,
    ...options,
  });
};

export const useIsSuccessorAuthorized = (
  options?: UseQueryOptions<SuccessorSessionResponse, any>
) => {
  const query = useCheckSuccessorSession(options);
  const authorized = query.data?.authorized === true;
  return { authorized, ...query };
};
